import React, { useRef, useEffect } from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import LinearProgress from "@mui/material/LinearProgress";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import QuizOutlinedIcon from "@mui/icons-material/QuizOutlined";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import Langs from "../../../../app/lang/langs";

/**
 * MobileNavigationMenu - Mobile step navigation with scrollable pill tabs,
 * progress indicator, and swipe hints.
 *
 * Accepts the exact same props as NavigationMenu.
 */
export default function MobileNavigationMenu({
    activeIndex,
    setActiveIndex,
    isMobile,
    mode,
    isInDatabase,
    projectName,
    onConfirmClose,
    onSaveDetails,
}) {
    const scrollRef = useRef(null);
    const activeChipRef = useRef(null);
    const isClickable = projectName !== "";

    const tabs = [
        ...(mode === "edit"
            ? [
                  {
                      index: 6,
                      label: <Langs str="PROGRESS" />,
                      icon: <TrendingUpIcon sx={{ fontSize: 16 }} />,
                      disabled: !isInDatabase,
                  },
              ]
            : []),
        {
            index: 0,
            label: "General",
            icon: <SettingsOutlinedIcon sx={{ fontSize: 16 }} />,
            disabled: !isClickable,
        },
        {
            index: 1,
            label: <Langs str="PUBLIC_FORMULAIRE" />,
            icon: <DescriptionOutlinedIcon sx={{ fontSize: 16 }} />,
            disabled: !isInDatabase,
        },
        {
            index: 2,
            label: <Langs str="PROJECT_QUESTIONS_BANK" />,
            icon: <QuizOutlinedIcon sx={{ fontSize: 16 }} />,
            disabled: !isInDatabase,
        },
        {
            index: 3,
            label: <><Langs str="EDIT_FORM" /> & MCQ</>,
            icon: <ListAltOutlinedIcon sx={{ fontSize: 16 }} />,
            disabled: !isInDatabase,
        },
        {
            index: 4,
            label: <Langs str="MEMBERS" />,
            icon: <PeopleOutlineIcon sx={{ fontSize: 16 }} />,
            disabled: !isInDatabase,
        },
        {
            index: 5,
            label: <Langs str="MANAGERS" />,
            icon: <AdminPanelSettingsOutlinedIcon sx={{ fontSize: 16 }} />,
            disabled: !isInDatabase,
        },
    ];

    const totalSteps = tabs.length;
    const currentStepIndex = tabs.findIndex((t) => t.index === activeIndex);
    const progressValue =
        totalSteps > 1
            ? (currentStepIndex / (totalSteps - 1)) * 100
            : 0;

    // Auto-scroll the active chip into view
    useEffect(() => {
        if (activeChipRef.current && scrollRef.current) {
            const container = scrollRef.current;
            const chip = activeChipRef.current;
            const chipLeft = chip.offsetLeft;
            const chipWidth = chip.offsetWidth;
            const containerWidth = container.offsetWidth;
            const scrollTarget = chipLeft - containerWidth / 2 + chipWidth / 2;
            container.scrollTo({ left: scrollTarget, behavior: "smooth" });
        }
    }, [activeIndex]);

    const handleTabClick = (tab) => {
        if (tab.disabled) return;
        setActiveIndex(tab.index);
    };

    return (
        <Box
            sx={{
                mt: 0,
                position: "sticky",
                top: 0,
                zIndex: 10,
                bgcolor: "#fff",
                borderBottom: "1px solid rgba(0,0,0,0.08)",
                px: 1,
                py: 1.5,
            }}
        >
            {/* Scrollable pill tabs with edge fade hints */}
            <Box sx={{ position: "relative" }}>
                {/* Left fade gradient */}
                <Box
                    sx={{
                        position: "absolute",
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: 24,
                        background:
                            "linear-gradient(to right, #fff 0%, transparent 100%)",
                        zIndex: 2,
                        pointerEvents: "none",
                    }}
                />
                {/* Right fade gradient */}
                <Box
                    sx={{
                        position: "absolute",
                        right: 0,
                        top: 0,
                        bottom: 0,
                        width: 24,
                        background:
                            "linear-gradient(to left, #fff 0%, transparent 100%)",
                        zIndex: 2,
                        pointerEvents: "none",
                    }}
                />

                <Box
                    ref={scrollRef}
                    sx={{
                        display: "flex",
                        gap: 1,
                        overflowX: "auto",
                        scrollSnapType: "x mandatory",
                        "&::-webkit-scrollbar": { display: "none" },
                        scrollbarWidth: "none",
                        pb: 0.5,
                        px: 2,
                    }}
                >
                    {tabs.map((tab) => {
                        const isActive = tab.index === activeIndex;
                        const isDisabled = tab.disabled;

                        return (
                            <Chip
                                key={tab.index}
                                ref={isActive ? activeChipRef : undefined}
                                icon={tab.icon}
                                label={tab.label}
                                clickable={!isDisabled}
                                onClick={() => handleTabClick(tab)}
                                variant={isActive ? "filled" : "outlined"}
                                sx={{
                                    scrollSnapAlign: "center",
                                    flexShrink: 0,
                                    height: 38,
                                    borderRadius: "999px",
                                    fontWeight: isActive ? 600 : 400,
                                    fontSize: "0.8125rem",
                                    letterSpacing: "0.01em",
                                    transition:
                                        "all 200ms cubic-bezier(0.4, 0, 0.2, 1)",
                                    cursor: isDisabled
                                        ? "default"
                                        : "pointer",
                                    opacity: isDisabled ? 0.4 : 1,
                                    pointerEvents: isDisabled
                                        ? "none"
                                        : "auto",

                                    // Active state
                                    ...(isActive && {
                                        bgcolor: "#FF9015",
                                        color: "#fff",
                                        borderColor: "#FF9015",
                                        boxShadow:
                                            "0 2px 8px rgba(255,144,21,0.35)",
                                        "& .MuiChip-icon": {
                                            color: "#fff",
                                        },
                                        "&:hover": {
                                            bgcolor: "#e8820f",
                                        },
                                    }),

                                    // Inactive state
                                    ...(!isActive && {
                                        bgcolor: "transparent",
                                        color: "#666",
                                        borderColor: "rgba(0,0,0,0.15)",
                                        "& .MuiChip-icon": {
                                            color: "#999",
                                        },
                                        "&:hover": {
                                            bgcolor: "rgba(255,144,21,0.06)",
                                            borderColor: "#FF9015",
                                            color: "#FF9015",
                                            "& .MuiChip-icon": {
                                                color: "#FF9015",
                                            },
                                        },
                                    }),

                                    // Tap feedback
                                    "&:active": {
                                        transform: "scale(0.96)",
                                    },
                                }}
                            />
                        );
                    })}
                </Box>
            </Box>

            {/* Step progress bar — hidden in edit mode */}
            {mode !== "edit" && (
                <Box sx={{ px: 2, mt: 1.5 }}>
                    <LinearProgress
                        variant="determinate"
                        value={progressValue}
                        sx={{
                            height: 3,
                            borderRadius: 2,
                            bgcolor: "rgba(0,0,0,0.06)",
                            "& .MuiLinearProgress-bar": {
                                borderRadius: 2,
                                bgcolor: "#FF9015",
                                transition:
                                    "transform 400ms cubic-bezier(0.4, 0, 0.2, 1)",
                            },
                        }}
                    />
                    {/* Dot indicators */}
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            gap: 0.75,
                            mt: 1,
                        }}
                    >
                        {tabs.map((tab, idx) => (
                            <Box
                                key={tab.index}
                                sx={{
                                    width: idx === currentStepIndex ? 18 : 6,
                                    height: 6,
                                    borderRadius: 3,
                                    bgcolor:
                                        idx === currentStepIndex
                                            ? "#FF9015"
                                            : tab.disabled
                                            ? "rgba(0,0,0,0.08)"
                                            : "rgba(0,0,0,0.15)",
                                    transition:
                                        "all 300ms cubic-bezier(0.4, 0, 0.2, 1)",
                                }}
                            />
                        ))}
                    </Box>
                </Box>
            )}
        </Box>
    );
}
